/**
 * BasicInfoDrawer — Edit area info form (name, type, building, max occupancy).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import Drawer from '../../../../../components/v2/Drawer.jsx';

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  areaName: initName, areaTypeId: initTypeId, buildingId: initBuildingId,
  maxOccupancy: initOccupancy, areaTypes, buildings,
}) => {
  const [areaName, setAreaName] = useState('');
  const [areaTypeId, setAreaTypeId] = useState(0);
  const [buildingId, setBuildingId] = useState(0);
  const [maxOccupancy, setMaxOccupancy] = useState(0);
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setAreaName(initName || '');
      setAreaTypeId(initTypeId || 0);
      setBuildingId(initBuildingId || 0);
      setMaxOccupancy(initOccupancy || 0);
      setErrors({});
    }
    prevOpen.current = open;
  });

  const validate = useCallback(() => {
    const errs = {};
    if (!areaName.trim()) {
      errs.areaName = 'Area name is required.';
    } else if (!/^[0-9a-zA-Z_\- ]+$/.test(areaName.trim())) {
      errs.areaName = 'Only letters, numbers, spaces, hyphens, and underscores.';
    }
    if (!areaTypeId) {
      errs.areaTypeId = 'Area type is required.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [areaName, areaTypeId]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      areaName: areaName.trim(),
      areaTypeId,
      buildingId,
      maxOccupancy,
    });
  }, [validate, onSave, areaName, areaTypeId, buildingId, maxOccupancy]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Edit Area Info"
      backdrop={false}
      bodyAs="form"
      bodyProps={{ onSubmit: handleSubmit }}
      footer={(
        <>
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      )}
    >
      {/* Area Name */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">
          Area Name <span className="dp-drawer__required">*</span>
        </label>
        <input
          type="text"
          className={`dp-drawer__input${errors.areaName ? ' is-invalid' : ''}`}
          value={areaName}
          onChange={(e) => setAreaName(e.target.value)}
          placeholder="Area name"
          autoFocus
        />
        {errors.areaName && <p className="dp-drawer__error">{errors.areaName}</p>}
      </div>

      {/* Area Type */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">
          Area Type <span className="dp-drawer__required">*</span>
        </label>
        <select
          className={`dp-drawer__select${errors.areaTypeId ? ' is-invalid' : ''}`}
          value={areaTypeId}
          onChange={(e) => setAreaTypeId(parseInt(e.target.value))}
        >
          <option value={0}>Select type…</option>
          {(areaTypes || []).map((t) => (
            <option key={t.area_type_id} value={t.area_type_id}>
              {t.area_type_description}
            </option>
          ))}
        </select>
        {errors.areaTypeId && <p className="dp-drawer__error">{errors.areaTypeId}</p>}
      </div>

      {/* Building */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">Building</label>
        <select
          className="dp-drawer__select"
          value={buildingId}
          onChange={(e) => setBuildingId(parseInt(e.target.value))}
        >
          <option value={0}>No Building</option>
          {(buildings || []).map((b) => (
            <option key={b.building_id} value={b.building_id}>
              {b.building_name}{b.address ? ` (${b.address})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Max Occupancy */}
      <div className="dp-drawer__field">
        <label className="dp-drawer__label">Max Occupancy</label>
        <input
          type="number"
          className="dp-drawer__input"
          value={maxOccupancy}
          onChange={(e) => setMaxOccupancy(parseInt(e.target.value) || 0)}
          min={0}
          max={100}
          step={1}
        />
      </div>

      {/* Footer */}
    </Drawer>
  );
};

export default BasicInfoDrawer;
